import { createFileRoute, Link } from "@tanstack/react-router";
import { Header, Footer } from "@/components/site-chrome";
import { ProductCarousel } from "@/components/product-carousel";
import { FaqSection } from "@/components/faq-section";
import { PolaroidGallery } from "@/components/polaroid-gallery";
import { MediaBand } from "@/components/media-band";
import { useState, useEffect } from "react";

const API = import.meta.env.VITE_API_URL || "https://pizzasteve-api.m-2396.workers.dev";

interface Product {
  id: string;
  name: string;
  size?: string;
  price?: number;
  priceLabel?: string;
  status: string;
  emoji: string;
  tag: string;
  imageUrl?: string;
}

export const Route = createFileRoute("/")(({
  head: () => ({
    meta: [
      { title: "Mr. Pizza Steve Finds - Vintage & Thrift | Zamalek, Cairo" },
      { name: "description", content: "Curated finds. If it's gone, it's gone forever. Vintage and thrift drops curated by Steve dos Santos in Zamalek, Cairo." },
      { name: "author", content: "Steve dos Santos" },
      { name: "theme-color", content: "#0a0a0a" },
      { property: "og:title", content: "Mr. Pizza Steve Finds - Vintage & Thrift | Zamalek, Cairo" },
      { property: "og:url", content: "https://mr.pizzastevefinds.com/" },
      { property: "og:image", content: "https://mr.pizzastevefinds.com/og-image.png" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Home,
}));

function Home() {
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    fetch(`${API}/api/products`)
      .then((r) => r.json())
      .then((data: Product[]) => setProducts(data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Header />

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section className="relative min-h-[90svh] w-full overflow-hidden sm:min-h-[100svh]">
        <img
          src="https://res.cloudinary.com/dnggmrgmu/image/upload/w_1200,f_auto,q_auto/v1782221376/market_sbt1ij.jpg"
          alt="Mr. Pizza Steve Finds"
          className="absolute inset-0 h-full w-full object-cover animate-hero-zoom"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-background/45" />

        <div className="relative z-10 flex min-h-[90svh] flex-col items-center justify-center px-6 text-center sm:min-h-[100svh]">
          <h1 className="text-[clamp(3rem,14vw,7rem)] font-black uppercase leading-[0.85] tracking-[-0.02em] text-foreground overflow-hidden">
            <span className="block overflow-hidden">
              <span className="block animate-word-in" style={{ animationDelay: "80ms" }}>Curated</span>
            </span>
            <span className="block overflow-hidden">
              <span className="block animate-word-in" style={{ animationDelay: "200ms" }}>Finds</span>
            </span>
          </h1>

          <p
            className="mt-5 text-[11px] uppercase tracking-[0.35em] text-zinc-100 font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] animate-fade-slide-up"
            style={{ animationDelay: "360ms" }}
          >
            If it's gone, it's gone forever.
          </p>

          <div className="mt-9 animate-fade-slide-up" style={{ animationDelay: "460ms" }}>
            <Link
              to="/shop"
              className="group relative inline-block overflow-hidden bg-foreground px-9 py-3.5 text-[11px] font-bold uppercase tracking-[0.25em] text-background transition-transform active:scale-[0.98]"
            >
              Shop Now
            </Link>
          </div>
        </div>
      </section>

      {/* ── MARQUEE ───────────────────────────────────────────────────────── */}
      <div className="overflow-hidden border-y border-border bg-background py-3">
        <div
          className="marquee-track flex w-max gap-12 text-sm font-bold uppercase tracking-[0.1em] text-foreground"
          style={{ animation: "marquee 20s linear infinite" }}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="whitespace-nowrap">Fresh Drops Every Weekend</span>
          ))}
        </div>
      </div>

      {/* ── NEW ARRIVALS x2 ──────────────────────────────────────────────── */}
      <ProductCarousel title="New Arrivals" products={products} />
      <ProductCarousel title="New Arrivals" products={[...products].reverse()} />

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <FaqSection />

      {/* ── POLAROID GALLERY ─────────────────────────────────────────────── */}
      <PolaroidGallery />

      {/* ── VIDEO / PHOTO BAND ───────────────────────────────────────────── */}
      <MediaBand />

      <Footer />
    </div>
  );
}
